import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, X, Loader2, AlertTriangle } from 'lucide-react';
import { superAdminApi } from '../../api/superAdmin.js';
import { overlayDismiss } from '../../lib/overlayDismiss';

/**
 * DeleteDealerModal — 刪除（停用）經銷商帳號的 double-confirm
 *
 * 防呆：必須一字不差輸入該經銷商 email 才能按刪除。
 * 後端 DELETE /parents/:uid = 停用 Firebase user + ivrMode=false + 移除全部 tenant membership
 * （不真刪資料，可由工程恢復）。
 */
export function DeleteDealerModal({ dealer, onClose, onDeleted }) {
    const { t } = useTranslation('superAdmin');
    const [typed, setTyped] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const match = typed.trim() === (dealer.email || '');
    const tenantCount = (dealer.tenants || []).length;
    const bizCount = (dealer.businessAccounts || []).length;

    const doDelete = async () => {
        if (!match || busy) return;
        setBusy(true);
        setError(null);
        try {
            await superAdminApi.deleteParent(dealer.uid);
            onDeleted?.();
        } catch (e) {
            setError(e.message || String(e));
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 p-4" {...overlayDismiss(onClose)}>
            <div
                className="w-full max-w-md rounded-xl bg-cream-50 border border-cream-200 shadow-xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-labelledby="del-dealer-title"
            >
                <div className="px-6 pt-5 pb-4 flex items-start gap-3">
                    <span className="grid place-items-center size-9 rounded-full bg-danger-100 shrink-0">
                        <AlertTriangle size={18} className="text-danger-500" />
                    </span>
                    <div className="min-w-0">
                        <h2 id="del-dealer-title" className="heading-display text-lg font-semibold text-ink-900">
                            {t('delete.title')}
                        </h2>
                        <p className="mt-1 text-sm text-ink-700 leading-relaxed">
                            {t('delete.warning', { email: dealer.email, tenants: tenantCount, businesses: bizCount })}
                        </p>
                        <p className="mt-1 text-xs text-ink-400">{t('delete.note')}</p>
                    </div>
                    <button onClick={onClose} className="ml-auto p-1 rounded text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition focus-visible:outline-2 focus-visible:outline-accent-500">
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 pb-2">
                    <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 mb-1.5">
                        {t('delete.typeToConfirm')}
                    </label>
                    <input
                        autoFocus
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doDelete()}
                        placeholder={dealer.email || ''}
                        className="w-full px-3 py-2 rounded-md border border-cream-300 bg-white text-sm font-mono text-ink-900 placeholder:text-ink-300 focus:outline-2 focus:outline-danger-500/60"
                    />
                    {error && <p className="mt-2 text-xs text-danger-500">{error}</p>}
                </div>

                <div className="px-6 py-4 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-3.5 py-1.5 rounded-md text-sm text-ink-700 hover:bg-cream-100 transition focus-visible:outline-2 focus-visible:outline-accent-500">
                        {t('delete.cancel')}
                    </button>
                    <button
                        onClick={doDelete}
                        disabled={!match || busy}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-danger-500 text-cream-50 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition focus-visible:outline-2 focus-visible:outline-danger-500 focus-visible:outline-offset-2"
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {t('delete.confirm')}
                    </button>
                </div>
            </div>
        </div>
    );
}
